import { encounterField } from '../../shared/array-types';
import { encounterZoneLabel } from './encounter-zone-label';
import { createFormulaReadContext } from './formula-read-context';
import { readItemField } from './item-field-reader';
import { createStatusLayerCompiler } from './status-layering-context';
import type { StatusLayeringMetadata } from './status-layering';
import { phpJsonInt } from './php-json-values';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { fromBaseClone, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { compileSurfacePictures, surfacePictureRows, surfaceField } from './from-base-encounter-media';
import { createEncounterStatusDefinitionProjection, createEncounterStatusDefinitionShellProjection, createEncounterStatusPresentationProjection } from './from-base-encounter-status';
import type { InventoryReferenceMetadata } from './from-base-inventory-references';
import type { InventoryMediaMetadata, InventoryVideoContext } from './from-base-inventory-media';

export interface FromBaseEncounterShellMetadata {
    definitionLookup?: (id: string) => Row | null;
    /** The read caller selects a display shell or the complete gameplay encounter. */
    projection?: 'shell' | 'full';
    layerCatalog: Parameters<typeof createFormulaReadContext>[0]['catalogRows'] & Readonly<Record<string, { aggregation_strategy?: string }>>;
    abilityTypes: readonly string[];
    statusLayering: StatusLayeringMetadata;
    shellCatalogs: Readonly<Record<string, readonly Row[]>>;
    cdnUrl?: string;
    /** Frontend reference registries for the separate raw status payload checkpoint. */
    statusDefinitions?: { references: InventoryReferenceMetadata; objectTypes: readonly string[]; media?: InventoryMediaMetadata };
}
export interface EncounterShellSource {
    entity_id: string;
    encounters: Row;
    statuses: { target: readonly Row[]; caster: readonly Row[] };
    /** SELECT-only joined location/master-parent columns, until C1 supplies them. */
    shell_context?: Row;
    missing_definition_ids?: readonly string[];
    pictures?: readonly Row[];
    media_context?: readonly Row[];
    /** Complete SELECT-only galleries for status definitions, including explicit misses. */
    status_definition_videos?: Omit<InventoryVideoContext, 'cdnUrl'>;
}
export const ENCOUNTER_SHELL_CONTEXT_FIELDS = ['encounter_master_parent_name', 'encounter_master_parent_type',
    'encounter_location_name', 'encounter_location_type', 'location_zone_prefix',
    'encounter_location_parent_id', 'encounter_location_parent_name', 'count_encounter_links'] as const;
const columns = ['encounter_private', 'encounter_deleted', 'encounter_created_by_id', 'encounter_master_parent_id',
    'encounter_master_parent_name', 'encounter_master_parent_type', 'encounter_location_parent_id', 'encounter_location_parent_name',
    'encounter_location_id', 'encounter_location_name', 'encounter_location_type', 'location_zone_prefix', 'encounter_id', 'encounter_name', 'encounter_description',
    'encounter_gm_notes', 'encounter_zone_prefix', 'encounter_zone_number', 'encounter_environment', 'encounter_inside',
    'encounter_weather', 'encounter_tempurature', 'encounter_wind', 'encounter_wind_direction', 'encounter_light',
    'encounter_backdrop_variant', 'encounter_interior_type', 'encounter_backdrop_seed', 'encounter_topography',
    'encounter_vegetation_type', 'encounter_vegetation_density', 'encounter_water_level', 'encounter_source_light',
    'encounter_max_x', 'encounter_max_y', 'encounter_max_z', 'encounter_ceiling', 'encounter_min_z', 'encounter_shape',
    'encounter_mx', 'encounter_mx_bucket', 'encounter_mx_combat_bucket', 'encounter_amb', 'encounter_amb_night',
    'encounter_amb_weather', 'current_round', 'current_encounter_creature_id_turn', 'in_combat', 'active_combat_id',
    'turn_phase', 'pending_main_turn_encounter_creature_id', 'legendary_turn_queue', 'encounter_explored', 'count_encounter_links'] as const;
const flags = ['encounter_private', 'encounter_deleted', 'in_combat', 'encounter_inside', 'encounter_explored'];
const nullable = ['encounter_location_parent_id', 'encounter_location_parent_name', 'encounter_mx', 'encounter_mx_bucket',
    'encounter_mx_combat_bucket', 'encounter_amb', 'encounter_amb_night', 'encounter_amb_weather', 'encounter_gm_notes',
    'encounter_zone_prefix', 'encounter_max_z', 'encounter_ceiling', 'encounter_min_z', 'encounter_shape',
    'current_encounter_creature_id_turn', 'active_combat_id', 'pending_main_turn_encounter_creature_id', 'legendary_turn_queue',
    'encounter_source_light', 'encounter_backdrop_variant', 'encounter_interior_type', 'encounter_backdrop_seed'];
const shellTypes: Readonly<Record<string, string>> = { encounter_environment: 'environment', encounter_light: 'light_type',
    encounter_weather: 'weather_type', encounter_tempurature: 'tempurature_type', encounter_wind: 'wind_type', encounter_location_type: 'location_type' };

/** Native build_encounter_arr's pure shell stages. No viewer perspective or persistent status consequences. */
export function compileEncounterShellFromBase(base: EncounterShellSource, catalog: Readonly<Record<string, Row>>,
    metadata?: FromBaseEncounterShellMetadata): { row: Row; statusProjection?: Row;
        statusDefinitionProjections?: Readonly<Record<string, Row>>; statusPresentationProjections?: Readonly<Record<string, Row>>;
        completedStages: string[]; missingStages: string[] } {
    const completedStages: string[] = [], missingStages: string[] = [];
    const source = { encounters: { ...base.encounters, ...base.shell_context } };
    const field = (name: string): unknown => encounterField(source, name) ?? null;
    const bucket: Row = Object.fromEntries(columns.map(name => [name, field(name)]));
    bucket.encounter_tempurature = field('encounter_tempurature') ?? field('encounter_temperature');
    bucket.turn_phase = field('turn_phase') ?? 'main';
    bucket.pending_main_turn_encounter_creature_id ??= '';
    bucket.legendary_turn_queue ??= '';
    const x = phpJsonFloat(field('encounter_max_x')), y = phpJsonFloat(field('encounter_max_y'));
    const minZ = field('encounter_min_z'), maxZ = field('encounter_max_z'), ceiling = field('encounter_ceiling');
    bucket.encounter_ceiling = isPhpNumeric(ceiling) && phpJsonFloat(ceiling) > 0 && isPhpNumeric(maxZ)
        && phpJsonFloat(maxZ) > 0 && phpJsonFloat(ceiling) <= phpJsonFloat(maxZ) ? ceiling : null;
    const computed: Row = {};
    if (ENCOUNTER_SHELL_CONTEXT_FIELDS.every(name => name in source.encounters)) {
        computed.encounter_zone = encounterZoneLabel(source, false);
        completedStages.push('encounter-shell-context', 'encounter-zone-label');
    } else missingStages.push('encounter-shell-context');
    Object.assign(computed, { encounter_range_min_x: -Math.floor((x > 0 ? x : 100) / 2),
        encounter_range_max_x: Math.ceil((x > 0 ? x : 100) / 2),
        encounter_range_min_y: -Math.floor((y > 0 ? y : 100) / 2), encounter_range_max_y: Math.ceil((y > 0 ? y : 100) / 2),
        encounter_range_min_z: isPhpNumeric(minZ) && phpJsonFloat(minZ) < 0 ? phpJsonFloat(minZ) : -100,
        encounter_range_max_z: phpJsonFloat(maxZ) > 0 ? maxZ : 1000 });
    completedStages.push('encounter-ranges');
    // Status twin anchors encounters through their pre-relocation top-level id.
    let row: Row = { ...bucket, computed: { encounter: computed } };
    let statusProjection: Row | undefined;
    let statusDefinitionProjections: Record<string, Row> | undefined;
    let statusPresentationProjections: Record<string, Row> | undefined;
    try {
        const pictures = surfacePictureRows(base.pictures, base.media_context);
        const own = compileSurfacePictures(pictures, base.entity_id, 'encounter_picture', metadata?.cdnUrl);
        if (own.length) row.pictures = { encounter_picture: own };
        for (const [fieldName, key] of [['encounter_location_id', 'encounter_location_map'], ['encounter_location_parent_id', 'location_parent_map']]) {
            const id = field(fieldName!);
            const maps = fromBaseUuid(id) ? compileSurfacePictures(pictures, id, 'location_map', metadata?.cdnUrl) : [];
            if (maps.length) row[key!] = maps;
        }
        completedStages.push('encounter-media-maps');
    } catch (error) {
        if (error instanceof FromBaseStageMissing) missingStages.push(error.message); else throw error;
    }
    if (base.statuses.target.length || base.statuses.caster.length) {
        if (!metadata) missingStages.push('encounter-status-metadata');
        else {
            if (metadata.statusDefinitions && !metadata.definitionLookup) {
                try {
                    const definition = createEncounterStatusDefinitionProjection(catalog, base.missing_definition_ids ?? [], metadata,
                        metadata.statusDefinitions.references, metadata.statusDefinitions.objectTypes);
                    statusProjection = compileEncounterStatusOverlay({ encounter_id: base.entity_id }, base.statuses, definition, metadata);
                    delete statusProjection.encounter_id;
                    completedStages.push('encounter-status-layer-projection');
                    const shells = createEncounterStatusDefinitionShellProjection(catalog, base.missing_definition_ids ?? [], metadata,
                        metadata.statusDefinitions.references, metadata.statusDefinitions.objectTypes);
                    const projections: Record<string, Row> = {};
                    for (const raw of [...base.statuses.target, ...base.statuses.caster]) {
                        const id = surfaceField(raw, 'status_item_id');
                        if (!fromBaseUuid(id)) throw new TypeError('Invalid status definition identity');
                        if (id.toUpperCase() in projections) continue;
                        const projected = shells(id);
                        if (projected) projections[id.toUpperCase()] = projected;
                    }
                    statusDefinitionProjections = projections;
                    completedStages.push('encounter-status-definition-shells');
                    if (metadata.statusDefinitions.media && base.status_definition_videos) {
                        if (typeof metadata.cdnUrl !== 'string') throw new FromBaseStageMissing('definition-videos', 'cdn-url');
                        const presentation = createEncounterStatusPresentationProjection(catalog, base.missing_definition_ids ?? [], metadata,
                            metadata.statusDefinitions.references, metadata.statusDefinitions.objectTypes, metadata.statusDefinitions.media,
                            { ...base.status_definition_videos, cdnUrl: metadata.cdnUrl });
                        const presented: Record<string, Row> = {};
                        for (const id of Object.keys(projections)) presented[id] = presentation(id)!;
                        statusPresentationProjections = presented;
                        completedStages.push('encounter-status-definition-media-normalization');
                    }
                } catch (error) {
                    if (error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) missingStages.push(error.message);
                    else throw error;
                }
            }
            const definition = (id: string): Row | null => {
                if (metadata.definitionLookup) return metadata.definitionLookup(id);
                const value = catalog[id] ?? catalog[id.toUpperCase()];
                if (value) {
                    if (Number(readItemField(value, 'item_deleted') ?? 0) !== 0) throw new TypeError(`Deleted encounter definition: ${id}`);
                    // Authored definitions are not yet fully finalized by C2a/C2b.
                    if (value.item_layer_types && Object.keys(value.item_layer_types).length)
                        throw new FromBaseStageMissing('encounter-status-definition-finalization', id);
                    return fromBaseClone(value);
                }
                if (base.missing_definition_ids?.some(miss => miss.toUpperCase() === id.toUpperCase()))
                    throw new FromBaseStageMissing('encounter-status-definition-input', id);
                throw new FromBaseInputMissing('definition', id);
            };
            try { row = compileEncounterStatusOverlay(row, base.statuses, definition, metadata);
                completedStages.push('encounter-status-overlay');
            } catch (error) {
                if (error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) missingStages.push(error.message);
                else throw error;
            }
        }
    } else completedStages.push('encounter-status-overlay');
    // Keep status-produced layers on the root, only relocate the native own-row columns.
    const outputBucket: Row = {};
    for (const name of columns) { outputBucket[name] = row[name]; delete row[name]; }
    for (const name of flags) if (phpJsonInt(outputBucket[name]) === 0) delete outputBucket[name];
    for (const name of nullable) if (outputBucket[name] == null || outputBucket[name] === '') delete outputBucket[name];
    for (const [name, type] of Object.entries(shellTypes)) {
        const value = encounterField({ encounters: outputBucket }, name);
        if (!fromBaseUuid(value)) continue;
        // Native get_item_id_name prefers the static catalog's identity over the authored row.
        const entry = metadata?.shellCatalogs[name]?.find(entry => String(readItemField(entry, 'item_id')).toUpperCase() === value.toUpperCase())
            ?? (name === 'encounter_location_type' ? catalog[value] ?? catalog[value.toUpperCase()] : undefined);
        if (entry) {
            outputBucket[name] = { items: { item_id: readItemField(entry, 'item_id'), item_name: readItemField(entry, 'item_name'), item_type: type } };
        } else if (name === 'encounter_location_type' || !metadata) missingStages.push(`encounter-reference-shell:${name}:${value}`);
    }
    // PHP inserts the computed cluster after pictures/status layers and the source bucket last.
    const { computed: derived, ...surface } = row;
    row = { ...surface, computed: derived, encounters: outputBucket };
    completedStages.push('encounter-own-row-normalization');
    return { row, statusProjection, statusDefinitionProjections, statusPresentationProjections,
        completedStages, missingStages: [...new Set(missingStages)] };
}

/** Status leaf over COMPLETED definitions, as roster totals consume completed members.
 * The raw constructor above keeps its finalization refusal until that owning stage is available. */
export function compileEncounterStatusOverlay(row: Row, saved: EncounterShellSource['statuses'],
    definition: (id: string) => Row | null, metadata: FromBaseEncounterShellMetadata): Row {
    const unavailable = (detail: string): never => { throw new FromBaseStageMissing('encounter-status-context', detail); };
    const deps = { catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: (name: string) => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: (id: string) => unavailable(`inventory:${id}`) };
    const readers = createFormulaReadContext(deps);
    const statuses = createStatusLayerCompiler({ ...deps, metadata: metadata.statusLayering,
        referenceInputs: { fullItem: definition, itemRow: definition,
            count: (table, _column, id) => table === 'items' ? (definition(id) ? 1 : 0) : unavailable(`inventory:${id}`),
            determineType: id => { definition(id); return 'item'; }, dynamicItem: id => unavailable(`origin:${id}`) },
        itemName: id => readers.itemField(definition(id), 'item_name'), fetchStatuses: () => unavailable('saved-rows'),
        campaignSeconds: () => unavailable('campaign-calendar'), casterTeam: id => unavailable(`caster-team:${String(id)}`) });
    return statuses(statuses(row, 'target', saved.target.map(normalizeSavedStatus)), 'caster', saved.caster.map(normalizeSavedStatus)) as Row;
}

/** build_status_arr runs BEFORE native layering: DECIMAL coordinates/distances become floats. */
export function normalizeSavedStatus(raw: Row): Row {
    const parts = ['year', 'month', 'day', 'hour', 'minute', 'second'];
    const floats = ['origin_point_x', 'origin_point_y', 'origin_point_z', 'distance_point_x', 'distance_point_y', 'distance_point_z',
        'aoe_distance', 'aoe_distance_depth', 'aoe_distance_height'].map(name => 'status_' + name);
    const columns = ['private', 'deleted', 'created_by_id', 'updated_by_id', 'id', 'item_id', 'shapeshift_monster_template_id',
        'caster_id', 'target_id', 'casting_targets_arr', 'aoe_type', 'aoe_source', 'aoe_geometry', 'opposing_team_only', 'damage_type',
        'forced_roll_extreme', 'stack', 'origin_id', 'connected_id', 'campaign_id', 'ends_on_target_start_of_turn',
        'ends_on_target_end_of_turn', 'ends_on_caster_start_of_next_turn', 'ends_on_caster_end_of_next_turn',
        ...['starts', 'ends', 'paused'].flatMap(prefix => parts.map(part => `${prefix}_campaign_${part}`))].map(name => 'status_' + name);
    const row: Row = { item_type: 'status_effect' };
    for (const name of [...columns, ...floats, 'item_name', 'status_origin_name']) {
        let value = surfaceField(raw, name) ?? null;
        if (name.startsWith('status_paused_campaign_')) value = value == null || value === '' || phpJsonInt(value) === 0 ? null : phpJsonInt(value);
        else if (name !== 'status_aoe_geometry' && (value === null || isPhpNumeric(value))) {
            if (floats.includes(name)) value = value === null ? 0 : phpJsonFloat(value);
            else if (name.startsWith('status_') && !['status_casting_targets_arr', 'status_shapeshift_monster_template_id'].includes(name))
                value = value === null ? 0 : phpJsonInt(value);
        }
        row[name] = value;
    }
    return row;
}
